/** biome-ignore-all lint/correctness/noUnusedImports: reason */

import React, { createContext, useContext, useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import { debugError, debugLog } from "../../helpers/debug";
import { useAuth } from "../auth";
import { useFirebase } from "../Firebase";
import { getObjFromLink, transformCollectionSnap } from "../helpers";
import { useKegs } from "../kegs";

const PartnersContext = createContext({});

export const PartnersProvider = ({ children }) => {
  const firebase = useFirebase();
  const { claims } = useAuth();
  const { partnerKegs } = useKegs();

  const [partners, setPartners] = useState();

  useEffect(() => {
    if (!claims?.companyLink) {
      debugLog("[PartnersProvider] No companyLink, skipping partners listener");
      return () => {};
    }

    debugLog("[PartnersProvider] Setting up partners listener");

    return firebase.partners(getObjFromLink(claims.companyLink).id).onSnapshot(
      snap => setPartners(transformCollectionSnap(snap)),
      err => debugLog(err)
    );
  }, [
    claims.companyLink,
    firebase,
  ]);

  const toggleLink = async (isLinked, partner) => {
    if (!claims?.companyLink) {
      debugError("[PartnersProvider] Cannot toggle link - no companyLink");
      return;
    }

    const currentTime = new Date();

    const companyPartnersRef = firebase.partners(
      getObjFromLink(claims.companyLink).id
    );

    const partnerPartnersRef = firebase.partners(partner.id);

    if (!isLinked) {
      const batch = firebase.db.batch();

      const companyPartner = {
        companyLink: `${partner.id}__${partner.name}`,
        companyObj: {
          id: partner.id,
          name: partner.name,
        },
        dateLinked: currentTime,
      };

      const partnerPartner = {
        companyLink: claims.companyLink,
        companyObj: getObjFromLink(claims.companyLink),
        dateLinked: currentTime,
      };

      await batch.set(companyPartnersRef.doc(), companyPartner);
      await batch.set(partnerPartnersRef.doc(), partnerPartner);

      return batch.commit();
    } else {
      const batch = firebase.db.batch();
      const kegsRef = await firebase.db.collection("kegs");

      if (!partner.companyLink) {
        partner.companyLink = `${partner.id}__${partner.name}`;
      }

      let partnerKegsCount = partnerKegs?.filter(
        k => k.partnerLink === partner.companyLink
      ).length;

      await kegsRef
        .where("companyLink", "==", partner.companyLink)
        .where("partnerLink", "==", claims.companyLink)
        .where("isArchive", "==", false)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(_ => {
            partnerKegsCount++;
          });
        });

      if (partnerKegsCount > 0) {
        toastr.error(
          "Oops",
          `Cannot unlink this partner currently, you still have ${partnerKegsCount} ${
            partnerKegsCount > 1 ? "kegs" : "keg"
          } shared between you.`
        );
      } else {
        if (partner.companyObj) {
          partner.id = partner.companyObj.id;
        }

        const companySnap = await companyPartnersRef
          .where("companyObj.id", "==", partner.id)
          .get();

        companySnap.forEach(async p => await batch.delete(p.ref));

        const partnerSnap = await partnerPartnersRef
          .where("companyLink", "==", claims.companyLink)
          .get();

        partnerSnap.forEach(async p => await batch.delete(p.ref));

        return await batch.commit();
      }
    }
  };

  return (
    <PartnersContext.Provider
      value={{
        partners,
        toggleLink,
      }}
    >
      {children}
    </PartnersContext.Provider>
  );
};

export const PartnersConsumer = PartnersContext.Consumer;

export const usePartners = () => useContext(PartnersContext);
